/**
 * Clasifica el fallo de una subida a S3 en **permanente** o **transitorio**.
 *
 * La distinción decide dos cosas a la vez y equivocarse en cualquiera de las dos direcciones cuesta:
 *
 *   · clasificar como permanente algo transitorio  → se pierde una foto que un reintento habría subido;
 *   · clasificar como transitorio algo permanente  → la cola nunca drena y al usuario se le pide
 *     reintentar algo que va a fallar igual, con el mensaje equivocado.
 *
 * El criterio no es el status HTTP. Un 403 puede ser las dos cosas: si la policy rechazó el tamaño es
 * permanente —el archivo no va a encoger—, pero si la firma venció es **transitorio**, porque la cola
 * vuelve a pedir una URL nueva antes de reintentar (`addPhotoAndDeclare` re-firma en cada intento).
 * Lo que decide es el `Code` que S3 devuelve en el XML.
 */

export type S3FailureKind = 'permanent' | 'transient';

export interface S3FailureVerdict {
  kind: S3FailureKind;
  /** El `Code` de S3 cuando se pudo leer; sirve para la telemetría y para el log. */
  code?: string;
  /** Mensaje para el usuario. Solo se usa cuando el veredicto es permanente. */
  userMessage: string;
}

/**
 * Condiciones de la policy que el mismo archivo va a volver a incumplir. `EntityTooLarge` y
 * `EntityTooSmall` son el `content-length-range`; `AccessDenied` acá es el `eq` del Content-Type,
 * que tampoco cambia entre intentos.
 */
const PERMANENT_CODES = new Set([
  'EntityTooLarge',
  'EntityTooSmall',
  'AccessDenied',
  'InvalidPolicyDocument',
]);

/**
 * Fallos donde re-firmar arregla el problema. Van a la cola **a propósito**: el reintento pide una
 * URL nueva, así que la firma vencida deja de estarlo.
 */
const TRANSIENT_CODES = new Set([
  'ExpiredToken',
  'RequestTimeTooSkewed',
  'TokenRefreshRequired',
  'InternalError',
  'SlowDown',
  'ServiceUnavailable',
]);

/** S3 responde XML; el cuerpo puede llegar como texto, como documento ya parseado, o no llegar. */
function extractCode(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const match = /<Code>([^<]+)<\/Code>/i.exec(body);
    return match ? match[1].trim() : undefined;
  }
  if (body && typeof body === 'object') {
    const asRecord = body as Record<string, unknown>;
    if (typeof asRecord['Code'] === 'string') {
      return (asRecord['Code'] as string).trim();
    }
  }
  return undefined;
}

function messageFor(code: string | undefined): string {
  switch (code) {
    case 'EntityTooLarge':
      return 'El archivo supera el tamaño máximo permitido. Selecciona uno más liviano.';
    case 'EntityTooSmall':
      return 'El archivo está vacío o es demasiado pequeño. Vuelve a capturarlo.';
    default:
      return 'El archivo no cumple con las condiciones de subida permitidas. Vuelve a capturarlo o selecciona otro.';
  }
}

/**
 * `status` es el código HTTP y `body` el cuerpo de la respuesta de S3.
 *
 * **Ante la duda, transitorio.** Un fallo no reconocido se reintenta y se encola: perder una foto que
 * se podía subir es peor que un reintento de más, y el reintento tiene tope. Este default es la razón
 * por la que la lista de permanentes es explícita y corta.
 */
export function classifyS3Failure(status: number | undefined, body?: unknown): S3FailureVerdict {
  const code = extractCode(body);

  if (code && PERMANENT_CODES.has(code)) {
    return { kind: 'permanent', code, userMessage: messageFor(code) };
  }
  if (code && TRANSIENT_CODES.has(code)) {
    return { kind: 'transient', code, userMessage: messageFor(code) };
  }

  // Sin `Code` legible: un 4xx que no es 408 ni 429 no mejora reintentando el mismo archivo.
  // Los 5xx y los de red sí, y un status ausente es una caída de conexión.
  if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) {
    return { kind: 'permanent', code, userMessage: messageFor(code) };
  }
  return { kind: 'transient', code, userMessage: messageFor(code) };
}
